import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import './IntroSlide.css';

// Import images
import mapImg2023 from '../images/images for slides/far right rise europe map and precentages as of end 2023.jpeg';
import mapImg2025 from '../images/images for slides/far right rise europe map and precentages as of end 2025.png';

function IntroSlide({ showNotes }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const images = [
    { src: mapImg2023, label: 'Ende 2023', alt: 'Far Right Rise in Europe Map - End of 2023' },
    { src: mapImg2025, label: 'Ende 2025', alt: 'Far Right Rise in Europe Map - End of 2025' },
  ];

  const oldVsNew = [
    { old: 'Glatze & Springerstiefel', new: 'Anzug & Krawatte' },
    { old: 'Isolierte Gruppen', new: 'Internationales Netzwerk' },
    { old: 'Straßenkampf', new: 'Kulturkampf online' },
    { old: 'Offener Rassismus', new: '"Ethnopluralismus"' },
  ];

  const connectingElements = [
    { icon: '🎯', text: 'Kampf gegen "Globalismus" und Migration' },
    { icon: '🌍', text: 'Ideologie des "Ethnopluralismus"' },
    { icon: '📱', text: 'Das Internet als Schlachtfeld' },
  ];

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleModalClick = (e) => {
    if (e.target.classList.contains('image-modal-overlay')) {
      closeModal();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
    } else if (e.key === 'ArrowRight') {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    } else if (e.key === 'ArrowLeft') {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  React.useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <div className="slide intro-slide">
      <div className="intro-layout">
        {/* Left Column */}
        <div className="intro-left">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            Einleitung
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Nicht mehr nur
            <br />
            <span className="highlight">"Springerstiefel"</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="question-box"
          >
            <span className="question-icon">❓</span>
            <p>Wenn ich "Rechtsextremist" sage – was stellt ihr euch vor?</p>
          </motion.div>

          {/* Comparison Cards */}
          <motion.div 
            className="comparison-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="comparison-header">
              <span className="old-label">Das alte Bild</span>
              <span className="arrow">→</span>
              <span className="new-label">Die Realität heute</span>
            </div>
            
            {oldVsNew.map((item, index) => (
              <motion.div
                key={index}
                className="comparison-row"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <span className="old-value">{item.old}</span>
                <span className="vs-arrow">→</span>
                <span className="new-value">{item.new}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right Column */}
        <div className="intro-right">
          {/* Image Carousel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="intro-image-container"
          >
            <div className="image-carousel">
              {/* Image Display */}
              <div className="carousel-image-wrapper" onClick={openModal}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].alt}
                    className="intro-content-image"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
                <div className="image-zoom-hint">
                  <ZoomIn size={16} />
                  <span>Klicken zum Vergrößern</span>
                </div>
              </div>

              {/* Carousel Controls */}
              <div className="carousel-controls">
                <button 
                  className="carousel-btn carousel-btn-prev"
                  onClick={prevImage}
                  aria-label="Vorheriges Bild"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="carousel-indicators">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      className={`carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Bild ${index + 1}: ${image.label}`}
                    >
                      <span className="indicator-label">{image.label}</span>
                    </button>
                  ))}
                </div>

                <button 
                  className="carousel-btn carousel-btn-next"
                  onClick={nextImage}
                  aria-label="Nächstes Bild"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Connecting Elements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="connecting-box"
          >
            <h3 className="connecting-title">Das verbindende Element</h3>
            <div className="connecting-list">
              {connectingElements.map((item, index) => (
                <motion.div
                  key={index}
                  className="connecting-item"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  <span className="item-emoji">{item.icon}</span>
                  <span>{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Metapolitik Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="metapolitik-box"
          >
            <div className="meta-header">
              <Brain size={20} />
              <span>Der Fachbegriff</span>
            </div>
            <h4 className="meta-term">Metapolitik</h4>
            <p className="meta-definition">
              "Erst die Kultur verändern, dann die Politik."
            </p>
            <p className="meta-explanation">
              Die Neue Rechte will nicht sofort Gesetze ändern, sondern zuerst 
              das Denken der Menschen beeinflussen.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="image-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleModalClick}
          >
            <motion.div
              className="image-modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <button className="modal-close-btn" onClick={closeModal}>
                <X size={24} />
              </button>

              <div className="modal-image-wrapper">
                <button 
                  className="modal-nav-btn modal-nav-prev"
                  onClick={prevImage}
                >
                  <ChevronLeft size={32} />
                </button>

                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].alt}
                    className="modal-image"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  />
                </AnimatePresence>

                <button 
                  className="modal-nav-btn modal-nav-next"
                  onClick={nextImage}
                >
                  <ChevronRight size={32} />
                </button>
              </div>

              <div className="modal-footer">
                <span className="modal-image-label">{images[currentImageIndex].label}</span>
                <div className="modal-indicators">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      className={`modal-indicator ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </div>
                <span className="modal-image-counter">
                  {currentImageIndex + 1} / {images.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Der Realitätscheck:</strong> Echte Nazis von 1933 trugen Anzüge und waren Akademiker (Goebbels). 
          Auch der "neue" Rechtsextremismus kommt intellektuell daher (Slim-Fit-Anzug, Podcast). Er greift nicht mit Panzern an, sondern über Algorithmen.<br/><br/>
          <strong>Metapolitik:</strong> Die Strategie, erst die "Köpfe" (Kultur) zu erobern, bevor man die Gesetze ändert. Begriffe wie "Remigration" werden normalisiert.<br/><br/>
          <strong>Die Rote Linie:</strong><br/>
          • <em>Konservativ:</em> Rechtsstaat, Ordnung, Grenzen (legitim).<br/>
          • <em>Rechtsextrem:</em> Menschenrechte gelten nur für das "eigene Volk". Andere werden entmenschlicht ("Invasoren").
        </div>
      )}
    </div>
  );
}

export default IntroSlide;